import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MapPin, Building2, ChevronRight, Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import api from '../../services/api';
import Modal from '../../components/shared/Modal';

/* ─── Constantes ─── */
const STATUS_EMP = [
  { value: 'lancamento', label: 'Lançamento' },
  { value: 'em_obras',   label: 'Em obras'   },
  { value: 'pronto',     label: 'Pronto'      },
  { value: 'entregue',   label: 'Entregue'    },
];

const STATUS_UNID = [
  { value: 'disponivel', label: 'Disponível', cls: 'bg-green-100 text-green-700'  },
  { value: 'reservada',  label: 'Reservada',  cls: 'bg-yellow-100 text-yellow-700' },
  { value: 'vendida',    label: 'Vendida',    cls: 'bg-red-100 text-red-700'      },
];

const EMP_BADGE = {
  lancamento: 'bg-blue-100 text-blue-700',
  em_obras:   'bg-orange-100 text-orange-700',
  pronto:     'bg-green-100 text-green-700',
  entregue:   'bg-gray-100 text-gray-700',
};

const EMPTY_EMP = {
  nome: '', endereco: '', bairro: '', cidade: 'Maringá', estado: 'PR', cep: '',
  status: 'lancamento', num_torres: 1, previsao_entrega: '', descricao: '',
};

const EMPTY_UNID = { identificador: '', torre: '', area_privativa: '', preco: '', num_vagas: '', status: 'disponivel' };

function fmtBRL(v) {
  if (!v) return '—';
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 });
}

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary';

/* ─── Componente principal ─── */
export default function EmpreendimentosList() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Modal empreendimento
  const [empModal, setEmpModal] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [empForm, setEmpForm] = useState(EMPTY_EMP);
  const [empErro, setEmpErro] = useState('');
  const [empTab, setEmpTab] = useState('dados');

  // Form nova unidade (dentro do modal empreendimento)
  const [showUnidForm, setShowUnidForm] = useState(false);
  const [unidForm, setUnidForm] = useState(EMPTY_UNID);
  const [unidErro, setUnidErro] = useState('');

  // Edição inline de DWV ID
  const [editingDwvId, setEditingDwvId] = useState(null); // unidade.id
  const [dwvIdInput, setDwvIdInput] = useState('');

  // Edição inline de unidade
  const [editingUnid, setEditingUnid] = useState(null); // { id, identificador, torre, area_privativa, preco, num_vagas }
  const [editUnidForm, setEditUnidForm] = useState({});
  const [editUnidErro, setEditUnidErro] = useState('');

  /* Queries */
  const { data: emps = [], isLoading } = useQuery({
    queryKey: ['empreendimentos'],
    queryFn: () => api.get('/empreendimentos').then(r => r.data.data || []),
  });

  const { data: unidades = [] } = useQuery({
    queryKey: ['unidades', editingEmp?.id],
    queryFn: () => api.get(`/unidades?empreendimento_id=${editingEmp.id}`).then(r => r.data.data || []),
    enabled: !!editingEmp?.id,
  });

  /* Mutations empreendimento */
  const saveEmp = useMutation({
    mutationFn: (body) => editingEmp
      ? api.put(`/empreendimentos/${editingEmp.id}`, body).then(r => r.data)
      : api.post('/empreendimentos', body).then(r => r.data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['empreendimentos'] });
      if (!editingEmp) {
        // Depois de criar, abrir em modo edição para adicionar unidades
        setEditingEmp(res.data);
        setEmpTab('unidades');
      } else {
        closeEmpModal();
      }
    },
    onError: (err) => setEmpErro(err.response?.data?.error || 'Erro ao salvar'),
  });

  /* Mutations unidade */
  const createUnid = useMutation({
    mutationFn: (body) => api.post('/unidades', body).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unidades', editingEmp?.id] });
      setUnidForm(EMPTY_UNID);
      setShowUnidForm(false);
      setUnidErro('');
    },
    onError: (err) => setUnidErro(err.response?.data?.error || 'Erro ao adicionar unidade'),
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }) => api.put(`/unidades/${id}/status`, { status }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['unidades', editingEmp?.id] }),
  });

  const deleteUnid = useMutation({
    mutationFn: (id) => api.delete(`/unidades/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['unidades', editingEmp?.id] }),
    onError: (err) => alert(err.response?.data?.error || 'Erro ao excluir'),
  });

  const saveDwvId = useMutation({
    mutationFn: ({ id, dwv_id }) => api.put(`/unidades/${id}`, { dwv_id: dwv_id || null }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unidades', editingEmp?.id] });
      setEditingDwvId(null);
    },
    onError: (err) => alert(err.response?.data?.error || 'Erro ao salvar DWV ID'),
  });

  /* Mutation editar unidade */
  const updateUnid = useMutation({
    mutationFn: ({ id, ...body }) => api.put(`/unidades/${id}`, body).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unidades', editingEmp?.id] });
      setEditingUnid(null);
      setEditUnidErro('');
    },
    onError: (err) => setEditUnidErro(err.response?.data?.error || 'Erro ao salvar'),
  });

  /* Handlers empreendimento */
  function openCreate() {
    setEditingEmp(null);
    setEmpForm(EMPTY_EMP);
    setEmpErro('');
    setEmpTab('dados');
    setShowUnidForm(false);
    setEmpModal(true);
  }

  function openEdit(emp, tab = 'dados') {
    setEditingEmp(emp);
    setEmpForm({
      nome: emp.nome, endereco: emp.endereco, bairro: emp.bairro || '',
      cidade: emp.cidade || 'Maringá', estado: emp.estado || 'PR', cep: emp.cep || '',
      status: emp.status, num_torres: emp.num_torres || 1,
      previsao_entrega: emp.previsao_entrega ? emp.previsao_entrega.slice(0, 10) : '',
      descricao: emp.descricao || '',
    });
    setEmpErro('');
    setEmpTab(tab);
    setShowUnidForm(false);
    setEmpModal(true);
  }

  function closeEmpModal() {
    setEmpModal(false);
    setEditingEmp(null);
    setEmpErro('');
    setShowUnidForm(false);
  }

  function setEF(k) { return e => setEmpForm(f => ({ ...f, [k]: e.target.value })); }

  function handleSaveEmp(e) {
    e.preventDefault();
    setEmpErro('');
    saveEmp.mutate(empForm);
  }

  /* Handlers unidade */
  function setUF(k) { return e => setUnidForm(f => ({ ...f, [k]: e.target.value })); }

  function handleAddUnid(e) {
    e.preventDefault();
    setUnidErro('');
    createUnid.mutate({
      empreendimento_id: editingEmp.id,
      identificador: unidForm.identificador,
      torre: unidForm.torre || null,
      area_privativa: unidForm.area_privativa ? Number(unidForm.area_privativa) : null,
      preco: unidForm.preco ? Number(unidForm.preco.replace(/\D/g, '')) : null,
      num_vagas: unidForm.num_vagas ? Number(unidForm.num_vagas) : null,
      status: unidForm.status,
    });
  }

  function nextStatus(current) {
    const idx = STATUS_UNID.findIndex(s => s.value === current);
    return STATUS_UNID[(idx + 1) % STATUS_UNID.length].value;
  }

  /* Stats unidades */
  const statsUnid = {
    total: unidades.length,
    disponivel: unidades.filter(u => u.status === 'disponivel').length,
    reservada:  unidades.filter(u => u.status === 'reservada').length,
    vendida:    unidades.filter(u => u.status === 'vendida').length,
  };

  if (isLoading) return <div className="p-8 text-center text-gray-400">Carregando...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Empreendimentos</h1>
        <button onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary">
          <Plus size={16} /> Novo Empreendimento
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {emps.map(emp => (
          <EmpCard key={emp.id} emp={emp}
            onEspelho={() => navigate(`/empreendimentos/${emp.id}/espelho`)}
            onEdit={() => openEdit(emp, 'dados')}
            onUnidades={() => openEdit(emp, 'unidades')}
          />
        ))}
        {emps.length === 0 && (
          <div className="col-span-3 py-16 text-center text-gray-400">
            Nenhum empreendimento cadastrado. Clique em "Novo Empreendimento" para começar.
          </div>
        )}
      </div>

      {/* Modal empreendimento */}
      <Modal isOpen={empModal} onClose={closeEmpModal}
        title={editingEmp ? `Editar — ${editingEmp.nome}` : 'Novo Empreendimento'}
        size="xl">

        {/* Tabs — unidades só disponível ao editar */}
        <div className="flex border-b border-gray-200 mb-6 -mt-2">
          {[{ key: 'dados', label: 'Dados' }, ...(editingEmp ? [{ key: 'unidades', label: `Unidades (${unidades.length})` }] : [])].map(t => (
            <button key={t.key} type="button" onClick={() => setEmpTab(t.key)}
              className={`px-5 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                empTab === t.key ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Aba Dados ── */}
        {empTab === 'dados' && (
          <form onSubmit={handleSaveEmp}>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do empreendimento *</label>
                <input value={empForm.nome} onChange={setEF('nome')} required className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço *</label>
                <input value={empForm.endereco} onChange={setEF('endereco')} required className={inputCls} placeholder="Rua, número, complemento" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                <input value={empForm.bairro} onChange={setEF('bairro')} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                <input value={empForm.cep} onChange={setEF('cep')} className={inputCls} placeholder="87010-000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                <input value={empForm.cidade} onChange={setEF('cidade')} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <input value={empForm.estado} onChange={setEF('estado')} className={inputCls} placeholder="PR" maxLength={2} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                <select value={empForm.status} onChange={setEF('status')} className={inputCls}>
                  {STATUS_EMP.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nº de Torres</label>
                <input type="number" min={1} value={empForm.num_torres} onChange={setEF('num_torres')} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Previsão de entrega</label>
                <input type="date" value={empForm.previsao_entrega} onChange={setEF('previsao_entrega')} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea value={empForm.descricao} onChange={setEF('descricao')} rows={3}
                  className={inputCls + ' resize-none'} />
              </div>
            </div>
            {empErro && <p className="text-red-500 text-sm mt-3">{empErro}</p>}
            <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-gray-100">
              <button type="button" onClick={closeEmpModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={saveEmp.isPending}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary disabled:opacity-50">
                {saveEmp.isPending ? 'Salvando...' : editingEmp ? 'Salvar alterações' : 'Criar empreendimento'}
              </button>
            </div>
          </form>
        )}

        {/* ── Aba Unidades ── */}
        {empTab === 'unidades' && editingEmp && (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'Total', val: statsUnid.total, cls: 'text-gray-700' },
                { label: 'Disponíveis', val: statsUnid.disponivel, cls: 'text-green-600' },
                { label: 'Reservadas',  val: statsUnid.reservada,  cls: 'text-yellow-600' },
                { label: 'Vendidas',    val: statsUnid.vendida,    cls: 'text-red-600'    },
              ].map(s => (
                <div key={s.label} className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className={`text-xl font-bold ${s.cls}`}>{s.val}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Tabela de unidades */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 text-left">Unidade</th>
                    <th className="px-4 py-2 text-left">Torre/Bloco</th>
                    <th className="px-4 py-2 text-left">Tamanho</th>
                    <th className="px-4 py-2 text-left">Valor</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Vagas</th>
                    <th className="px-4 py-2 text-left">DWV ID</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {unidades.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400 text-xs">Nenhuma unidade cadastrada</td></tr>
                  )}
                  {unidades.map(u => {
                    const st = STATUS_UNID.find(s => s.value === u.status) || STATUS_UNID[0];
                    const isEditing = editingUnid === u.id;
                    return (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 font-medium text-gray-800">
                          {isEditing ? (
                            <input value={editUnidForm.identificador || ''} onChange={e => setEditUnidForm(f => ({ ...f, identificador: e.target.value }))}
                              className="w-20 border border-secondary rounded px-2 py-1 text-sm focus:outline-none" />
                          ) : u.identificador}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500">
                          {isEditing ? (
                            <input value={editUnidForm.torre || ''} onChange={e => setEditUnidForm(f => ({ ...f, torre: e.target.value }))}
                              placeholder="Torre" className="w-20 border border-secondary rounded px-2 py-1 text-sm focus:outline-none" />
                          ) : u.torre || '—'}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {isEditing ? (
                            <input type="number" step="0.01" min="0" value={editUnidForm.area_privativa || ''}
                              onChange={e => setEditUnidForm(f => ({ ...f, area_privativa: e.target.value }))}
                              placeholder="m²" className="w-20 border border-secondary rounded px-2 py-1 text-sm focus:outline-none" />
                          ) : u.area_privativa ? `${u.area_privativa} m²` : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {isEditing ? (
                            <input type="number" step="1" min="0" value={editUnidForm.preco || ''}
                              onChange={e => setEditUnidForm(f => ({ ...f, preco: e.target.value }))}
                              placeholder="R$" className="w-24 border border-secondary rounded px-2 py-1 text-sm focus:outline-none" />
                          ) : fmtBRL(u.preco)}
                        </td>
                        <td className="px-4 py-2.5">
                          {isEditing ? (
                            <select value={editUnidForm.status || u.status} onChange={e => setEditUnidForm(f => ({ ...f, status: e.target.value }))}
                              className="border border-secondary rounded px-2 py-1 text-sm focus:outline-none">
                              {STATUS_UNID.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                          ) : (
                            <button
                              onClick={() => updateStatus.mutate({ id: u.id, status: nextStatus(u.status) })}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer transition-opacity hover:opacity-70 ${st.cls}`}
                              title="Clique para alterar status"
                            >
                              {st.label}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          {isEditing ? (
                            <input type="number" min="0" value={editUnidForm.num_vagas ?? ''}
                              onChange={e => setEditUnidForm(f => ({ ...f, num_vagas: e.target.value }))}
                              placeholder="Vagas" className="w-16 border border-secondary rounded px-2 py-1 text-sm focus:outline-none" />
                          ) : u.num_vagas ?? '—'}
                        </td>
                        <td className="px-4 py-2.5">
                          {editingDwvId === u.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                autoFocus
                                value={dwvIdInput}
                                onChange={e => setDwvIdInput(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') saveDwvId.mutate({ id: u.id, dwv_id: dwvIdInput });
                                  if (e.key === 'Escape') setEditingDwvId(null);
                                }}
                                placeholder="ID no DWV"
                                className="w-24 border border-secondary rounded px-2 py-1 text-xs focus:outline-none"
                              />
                              <button onClick={() => saveDwvId.mutate({ id: u.id, dwv_id: dwvIdInput })}
                                className="text-xs text-green-600 hover:text-green-800 font-medium">✓</button>
                              <button onClick={() => setEditingDwvId(null)}
                                className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingDwvId(u.id); setDwvIdInput(u.dwv_id || ''); }}
                              className={`text-xs px-2 py-1 rounded border transition-colors ${
                                u.dwv_id
                                  ? 'border-indigo-200 text-indigo-600 bg-indigo-50 hover:bg-indigo-100'
                                  : 'border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600'
                              }`}
                              title="Clique para definir o ID da unidade no DWV"
                            >
                              {u.dwv_id || '+ vincular'}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {isEditing ? (
                            <div className="flex items-center gap-1 justify-end">
                              <button onClick={() => updateUnid.mutate({ id: u.id, ...editUnidForm })}
                                className="text-green-600 hover:text-green-800 p-1 rounded" title="Salvar">
                                <Check size={14} />
                              </button>
                              <button onClick={() => { setEditingUnid(null); setEditUnidErro(''); }}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded" title="Cancelar">
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 justify-end">
                              <button onClick={() => { setEditingUnid(u.id); setEditUnidForm({ identificador: u.identificador, torre: u.torre || '', area_privativa: u.area_privativa || '', preco: u.preco || '', status: u.status, num_vagas: u.num_vagas ?? '' }); }}
                                className="text-gray-300 hover:text-primary p-1 rounded" title="Editar">
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => { if (window.confirm(`Excluir unidade ${u.identificador}?`)) deleteUnid.mutate(u.id); }}
                                className="text-gray-300 hover:text-red-400 p-1 rounded"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Formulário nova unidade */}
            {showUnidForm ? (
              <form onSubmit={handleAddUnid} className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700">Nova unidade</p>
                <div className="grid grid-cols-6 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Número/ID *</label>
                    <input value={unidForm.identificador} onChange={setUF('identificador')} required
                      placeholder="101, Lote 5..." className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Torre/Bloco</label>
                    <input value={unidForm.torre} onChange={setUF('torre')} placeholder="Torre A" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Tamanho (m²)</label>
                    <input type="number" step="0.01" min="0" value={unidForm.area_privativa} onChange={setUF('area_privativa')}
                      placeholder="72.50" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Valor (R$)</label>
                    <input type="number" step="1" min="0" value={unidForm.preco} onChange={setUF('preco')}
                      placeholder="350000" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Vagas</label>
                    <input type="number" min="0" value={unidForm.num_vagas || ''} onChange={setUF('num_vagas')}
                      placeholder="1" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Status</label>
                    <select value={unidForm.status} onChange={setUF('status')} className={inputCls}>
                      {STATUS_UNID.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
                {unidErro && <p className="text-red-500 text-xs">{unidErro}</p>}
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => { setShowUnidForm(false); setUnidErro(''); }}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
                    Cancelar
                  </button>
                  <button type="submit" disabled={createUnid.isPending}
                    className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-secondary disabled:opacity-50">
                    {createUnid.isPending ? 'Salvando...' : 'Adicionar'}
                  </button>
                </div>
              </form>
            ) : (
              <button onClick={() => setShowUnidForm(true)}
                className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-primary hover:text-primary transition-colors">
                <Plus size={16} /> Adicionar unidade
              </button>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ─── Card de empreendimento ─── */
function EmpCard({ emp, onEspelho, onEdit, onUnidades }) {
  const STATUS_LABELS = { lancamento: 'Lançamento', em_obras: 'Em obras', pronto: 'Pronto', entregue: 'Entregue' };

  return (
    <div className="bg-white rounded-xl shadow hover:shadow-lg transition-shadow">
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="p-2 bg-blue-50 rounded-lg"><Building2 size={24} className="text-primary" /></div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${EMP_BADGE[emp.status] || 'bg-gray-100 text-gray-600'}`}>
              {STATUS_LABELS[emp.status] || emp.status}
            </span>
            <button onClick={onEdit} title="Editar" className="text-gray-300 hover:text-primary p-1 rounded">
              <Pencil size={14} />
            </button>
          </div>
        </div>
        <h2 className="font-bold text-gray-800 mt-2">{emp.nome}</h2>
        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
          <MapPin size={13} />
          <span>{emp.bairro || emp.cidade} — {emp.cidade}/{emp.estado}</span>
        </div>
        <p className="text-xs text-gray-400 mt-1 truncate">{emp.endereco}</p>
      </div>
      <div className="px-6 pb-4 border-t pt-4 flex gap-2">
        <button onClick={onUnidades}
          className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg py-2 hover:bg-gray-50 hover:text-primary transition-colors">
          <Plus size={14} /> Unidades
        </button>
        <button onClick={onEspelho}
          className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium text-primary border border-primary rounded-lg py-2 hover:bg-blue-50 transition-colors">
          Espelho <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
